<script context="module" lang="ts">
    import {
        DESKTOP_TRAY_COLUMNS as TRAY_COLUMNS,
        DESKTOP_TRAY_ROWS as TRAY_ROWS,
        TABLE_COLUMNS,
        TABLE_ROWS,
    } from "../misc/trayLayout";

    export { TABLE_COLUMNS, TABLE_ROWS, TRAY_COLUMNS, TRAY_ROWS };

    type Position = {
        x: number;
        y: number;
    };

    export type GridStackEvent = {
        src: Position;
        dst: Position;
    };
</script>

<script lang="ts">
    import "gridstack/dist/gridstack.min.css";
    import { GridItemHTMLElement, GridStack, GridStackNode } from "gridstack";
    import "gridstack/dist/h5/gridstack-dd-native";

    import { onMount, createEventDispatcher } from "svelte";
    import { getRuntimeTrayLayout } from "../misc/trayLayout";

    const dispatch = createEventDispatcher<{
        moveTableTile: GridStackEvent;
        moveTrayTile: GridStackEvent;
        addTile: GridStackEvent;
        removeTile: GridStackEvent;
    }>();

    let tableGrid: GridStack;
    let trayGrid: GridStack;

    export let showTableChrome = true;
    export let showTrayHelp = true;

    export const getTableGrid = () => tableGrid;
    export const getTrayGrid = () => trayGrid;

    type DragState = {
        originalPos: Position;
        multiSelectedTiles: GridItemHTMLElement[];
    };
    let dragState: DragState = null;

    onMount(() => {
        tableGrid = initTableGrid();
        trayGrid = initTrayGrid();

        setupTableEvents();
        setupTrayEvents();
        setupTileTransferEvents();
    });

    const initTableGrid = () => {
        const tableOptions = {
            disableOneColumnMode: true,
            disableResize: true,
            float: true,
            margin: "12px 1px",
            acceptWidgets: true,
            row: TABLE_ROWS,
            column: TABLE_COLUMNS,
        };
        return GridStack.init(tableOptions, "#table");
    };

    const initTrayGrid = () => {
        const { columns, rows } = getRuntimeTrayLayout();
        const trayOptions = {
            disableOneColumnMode: true,
            disableResize: true,
            float: true,
            margin: "12px 1px",
            acceptWidgets: true,
            row: rows,
            column: columns,
        };
        return GridStack.init(trayOptions, "#tray");
    };

    // ---------------------
    // --* EVENT HANDLERS
    // ---------------------

    const setupTableEvents = () => {
        // Pick up table tile
        tableGrid.on("dragstart", (event: any, el: GridItemHTMLElement) => {
            const originalPos = {
                x: el.gridstackNode.x,
                y: el.gridstackNode.y,
            };
            dragState = {
                originalPos,
                multiSelectedTiles: null,
            };

            if (event.shiftKey) {
                dragState.multiSelectedTiles = multiSelectTiles(
                    tableGrid,
                    originalPos
                );
            }
        });

        // Place down table tile
        tableGrid.on("dragstop", (event: any, el: GridItemHTMLElement) => {
            const originalPos = dragState.originalPos;
            const newX = el.gridstackNode.x;
            const newY = el.gridstackNode.y;

            // Unselects tiles, if we have any to unselect
            if (dragState.multiSelectedTiles !== null)
                unselectSelectedTiles(dragState.multiSelectedTiles);

            if (originalPos.x === newX && originalPos.y === newY) return;

            dispatch("moveTableTile", {
                src: originalPos,
                dst: { x: newX, y: newY },
            });

            // Handles multi-selected items
            if (dragState.multiSelectedTiles !== null) {
                const multiSelectedTiles = dragState.multiSelectedTiles;

                // Move selected tiles
                for (let i = 0; i < multiSelectedTiles.length; i++) {
                    const selectedTile = multiSelectedTiles[i];
                    const offsetX = i + 1;

                    const selectedTileNewPos: Position = {
                        x: newX + offsetX,
                        y: newY,
                    };
                    const selectedTileOldPos: Position = {
                        x: originalPos.x + offsetX,
                        y: originalPos.y,
                    };

                    const itWillFit = isTileOnTableAtPos(selectedTileNewPos);
                    if (!itWillFit) break;

                    // Move tile
                    tableGrid.update(selectedTile, selectedTileNewPos);

                    dispatch("moveTableTile", {
                        src: selectedTileOldPos,
                        dst: selectedTileNewPos,
                    });
                }
            }
        });
    };

    const setupTrayEvents = () => {
        // Pick up tray tile
        trayGrid.on("dragstart", (event: any, el: GridItemHTMLElement) => {
            const originalPos = {
                x: el.gridstackNode.x,
                y: el.gridstackNode.y,
            };
            dragState = {
                originalPos,
                multiSelectedTiles: [],
            };

            if (event.shiftKey) {
                dragState.multiSelectedTiles = multiSelectTiles(
                    trayGrid,
                    originalPos
                );
            }
        });

        // Place down tray tile
        trayGrid.on("dragstop", (event: any, el: GridItemHTMLElement) => {
            const originalPos = dragState.originalPos;
            const newX = el.gridstackNode.x;
            const newY = el.gridstackNode.y;

            dispatch("moveTrayTile", {
                src: originalPos,
                dst: { x: newX, y: newY },
            });

            // Handles multi-selected items
            if (dragState.multiSelectedTiles !== null) {
                const multiSelectedTiles = dragState.multiSelectedTiles;

                unselectSelectedTiles(multiSelectedTiles);

                // Move selected tiles
                for (let i = 0; i < multiSelectedTiles.length; i++) {
                    const selectedTile = multiSelectedTiles[i];
                    const offsetX = i + 1;

                    const selectedTileNewPos: Position = {
                        x: newX + offsetX,
                        y: newY,
                    };
                    const selectedTileOldPos: Position = {
                        x: originalPos.x + offsetX,
                        y: originalPos.y,
                    };

                    const itWillFit = isTileOnTrayAtPos(selectedTileNewPos);
                    if (!itWillFit) break;

                    // Move tile
                    trayGrid.update(selectedTile, selectedTileNewPos);

                    dispatch("moveTrayTile", {
                        src: selectedTileOldPos,
                        dst: selectedTileNewPos,
                    });
                }
            }
        });
    };

    const setupTileTransferEvents = () => {
        // Tray --> table
        tableGrid.on(
            "dropped",
            (
                event: any,
                previousWidget: GridStackNode,
                newWidget: GridStackNode
            ) => {
                const originalPos = dragState.originalPos;
                dispatch("addTile", {
                    src: { x: originalPos.x, y: originalPos.y },
                    dst: { x: newWidget.x, y: newWidget.y },
                });

                // Tile is now staging
                newWidget.el.children[0].className += " staging-tile";

                // Handles multi-selected items
                if (dragState.multiSelectedTiles !== null) {
                    const multiSelectedTiles = dragState.multiSelectedTiles;

                    unselectSelectedTiles(multiSelectedTiles);

                    // Move selected tiles
                    for (let i = 0; i < multiSelectedTiles.length; i++) {
                        const selectedTile = multiSelectedTiles[i];
                        const offsetX = i + 1;

                        const selectedTileNewPos: Position = {
                            x: newWidget.x + offsetX,
                            y: newWidget.y,
                        };
                        const selectedTileOldPos: Position = {
                            x: originalPos.x + offsetX,
                            y: originalPos.y,
                        };

                        const itWillFit = isTileOnTableAtPos(
                            selectedTileNewPos
                        );
                        if (!itWillFit) break;

                        // Move tile
                        const widget = tableGrid.addWidget(
                            selectedTile,
                            selectedTileNewPos
                        );

                        // Set tile to staging
                        widget.children[0].className += " staging-tile";

                        dispatch("addTile", {
                            src: selectedTileOldPos,
                            dst: selectedTileNewPos,
                        });
                    }
                }
            }
        );

        // Table --> tray
        trayGrid.on(
            "dropped",
            (
                event: any,
                previousWidget: GridStackNode,
                newWidget: GridStackNode
            ) => {
                const originalPos = dragState.originalPos;
                dispatch("removeTile", {
                    src: { x: originalPos.x, y: originalPos.y },
                    dst: { x: newWidget.x, y: newWidget.y },
                });

                // Tile is no longer staging
                const item = newWidget.el.children[0];
                item.className = item.className.replace("staging-tile", "");

                // Handles multi-selected items
                if (dragState.multiSelectedTiles !== null) {
                    const multiSelectedTiles = dragState.multiSelectedTiles;

                    unselectSelectedTiles(multiSelectedTiles);

                    // Move selected tiles
                    for (let i = 0; i < multiSelectedTiles.length; i++) {
                        const selectedTile = multiSelectedTiles[i];
                        const offsetX = i + 1;

                        const selectedTileNewPos: Position = {
                            x: newWidget.x + offsetX,
                            y: newWidget.y,
                        };
                        const selectedTileOldPos: Position = {
                            x: originalPos.x + offsetX,
                            y: originalPos.y,
                        };

                        const itWillFit = isTileOnTrayAtPos(selectedTileNewPos);
                        if (!itWillFit) break;

                        // Move tile
                        const widget = trayGrid.addWidget(
                            selectedTile,
                            selectedTileNewPos
                        );

                        // Unset tile from staging
                        const item = widget.children[0];
                        item.className = item.className.replace(
                            "staging-tile",
                            ""
                        );

                        dispatch("removeTile", {
                            src: selectedTileOldPos,
                            dst: selectedTileNewPos,
                        });
                    }
                }
            }
        );
    };

    // ----------------
    // --* FUNCTIONS
    // ----------------

    const multiSelectTiles = (
        grid: GridStack,
        originalPos: Position
    ): GridItemHTMLElement[] => {
        const items = grid.getGridItems();

        // Gets adjacent items
        let itemsInSet: GridItemHTMLElement[] = [];
        let currentX = originalPos.x + 1;
        for (let i = 0; i < Math.max(TABLE_COLUMNS, TRAY_COLUMNS); i++) {
            const itemsAtX = items.filter(
                (item) =>
                    item.gridstackNode.y === originalPos.y &&
                    item.gridstackNode.x === currentX
            );

            if (itemsAtX.length > 0) {
                const itemAtX = itemsAtX[0];
                itemsInSet = [...itemsInSet, itemAtX];
                currentX++;
            } else {
                break;
            }
        }

        // Makes them all 'selected'
        for (const itemInSet of itemsInSet) {
            itemInSet.className += " selected-tile";
        }

        return itemsInSet;
    };

    const unselectSelectedTiles = (selectedTiles: GridItemHTMLElement[]) => {
        for (const selectedTile of selectedTiles) {
            selectedTile.className = selectedTile.className.replace(
                "selected-tile",
                ""
            );
        }
    };

    const isTileOnTableAtPos = (pos: Position) => {
        return tableGrid.isAreaEmpty(pos.x, pos.y, 1, 1);
    };

    const isTileOnTrayAtPos = (pos: Position) => {
        return trayGrid.isAreaEmpty(pos.x, pos.y, 1, 1);
    };
</script>

<div class="board-layout">
    <section class="table-shell">
        {#if showTableChrome}
            <div class="surface-head">
                <div>
                    <p class="surface-label">Shared board</p>
                    <h2>Table</h2>
                </div>
                <div class="surface-legend">
                    <span><i class="legend-swatch selected"></i>Selected meld</span>
                    <span><i class="legend-swatch staged"></i>Staged tile</span>
                </div>
            </div>
        {/if}
        <div class="grid-stack" id="table" aria-label="table" />
    </section>

    <section class="tray-shell" class:compact-tray={!showTrayHelp}>
        {#if showTrayHelp}
            <div class="tray-meta">
                <div>
                    <p class="surface-label">Your rack</p>
                    <h2>Tray</h2>
                </div>
                <span>Shift-drag moves adjacent tiles together.</span>
            </div>
        {/if}
        <div class="grid-stack" id="tray" aria-label="tray" />
    </section>
</div>

<style>
    .board-layout {
        display: grid;
        gap: 16px;
        min-height: calc(100vh - var(--tray-reserved-space, 270px));
    }

    .table-shell,
    .tray-shell {
        border-radius: var(--radius-lg);
        border: 1px solid rgba(126, 173, 255, 0.16);
        box-shadow: var(--shadow-soft);
        overflow: hidden;
    }

    .table-shell {
        padding: var(--board-shell-pad, 14px);
        background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.04), transparent 16%),
            linear-gradient(180deg, rgba(13, 31, 55, 0.96), rgba(6, 18, 34, 0.96));
    }

    .tray-shell {
        position: fixed;
        left: var(--game-edge, 14px);
        right: var(--game-edge, 14px);
        bottom: var(--game-edge, 14px);
        z-index: 9;
        padding: var(--tray-shell-pad, 12px);
        background:
            linear-gradient(180deg, rgba(177, 110, 57, 0.1), transparent 18%),
            linear-gradient(180deg, rgba(87, 44, 21, 0.98), rgba(58, 27, 13, 0.98));
    }

    .compact-tray {
        padding-top: var(--tray-shell-pad-compact, 10px);
    }

    .surface-head,
    .tray-meta {
        display: flex;
        justify-content: space-between;
        align-items: end;
        gap: 16px;
        margin-bottom: 16px;
    }

    .surface-label {
        margin: 0 0 6px;
        color: var(--text-dim);
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.16em;
    }

    h2 {
        margin: 0;
        font-size: 1.35rem;
    }

    .surface-legend,
    .tray-meta span {
        color: var(--text-muted);
        font-size: 0.85rem;
    }

    .surface-legend {
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
    }

    .surface-legend span {
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

    .legend-swatch {
        width: 18px;
        height: 18px;
        border-radius: var(--radius-sm);
        display: inline-block;
        border: 1px solid rgba(126, 173, 255, 0.3);
    }

    .legend-swatch.selected {
        box-shadow: 0 0 0 2px rgba(63, 140, 255, 0.28);
        background: rgba(63, 140, 255, 0.16);
    }

    .legend-swatch.staged {
        border-color: rgba(255, 207, 99, 0.4);
        background: rgba(255, 207, 99, 0.16);
    }

    #table {
        min-height: var(--table-grid-min-height, max(460px, calc(100vh - var(--tray-reserved-space, 270px) - 138px)));
        min-width: var(--table-min-width, 0);
        border-radius: var(--radius-md);
        position: relative;
        background:
            radial-gradient(circle at top, rgba(81, 139, 244, 0.22), transparent 24%),
            radial-gradient(circle at center, rgba(46, 94, 172, 0.18), transparent 65%),
            repeating-linear-gradient(
                90deg,
                rgba(255, 255, 255, 0.028) 0,
                rgba(255, 255, 255, 0.028) 1px,
                transparent 1px,
                transparent calc(100% / 24)
            ),
            repeating-linear-gradient(
                180deg,
                rgba(255, 255, 255, 0.02) 0,
                rgba(255, 255, 255, 0.02) 1px,
                transparent 1px,
                transparent 72px
            ),
            linear-gradient(180deg, #12325e, #081a31);
        border: 1px solid rgba(126, 173, 255, 0.12);
        padding: 6px;
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.08),
            inset 0 0 0 1px rgba(34, 73, 124, 0.22),
            inset 0 -30px 70px rgba(4, 10, 20, 0.32);
    }

    #table::after {
        content: "Build runs and groups here";
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        font-family: var(--title-font);
        font-size: clamp(1.1rem, 2vw, 1.6rem);
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: rgba(186, 213, 255, 0.08);
        pointer-events: none;
    }

    #tray {
        min-height: var(--tray-grid-height, 174px);
        height: var(--tray-grid-height, 174px);
        border-radius: var(--radius-md);
        position: relative;
        background:
            linear-gradient(180deg, rgba(237, 197, 148, 0.24), transparent 20%),
            linear-gradient(
                180deg,
                transparent 0,
                transparent 53%,
                rgba(58, 27, 9, 0.32) 53%,
                rgba(58, 27, 9, 0.32) 56%,
                transparent 56%,
                transparent 100%
            ),
            linear-gradient(180deg, rgba(162, 98, 52, 0.94), rgba(102, 57, 28, 0.98));
        border: 1px solid rgba(246, 206, 165, 0.12);
        padding: 10px;
        box-shadow:
            inset 0 1px 0 rgba(255, 240, 220, 0.2),
            inset 0 -10px 24px rgba(59, 27, 9, 0.22),
            inset 0 32px 26px rgba(255, 237, 213, 0.04);
    }

    @media (max-width: 860px) {
        .board-layout {
            gap: 10px;
        }

        .table-shell {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
        }

        .surface-head,
        .tray-meta {
            flex-direction: column;
            align-items: flex-start;
        }

        #table {
            min-width: 980px;
            min-height: var(--table-grid-min-height, 430px);
        }
    }
</style>
